/**
 * NetworkTopologyCanvas — Topología visual de red
 *
 * Muestra switches → patch panels → servidores (NICs) y dispositivos terminales
 * (workstations, teléfonos, impresoras) conectados a través de cables registrados.
 *
 * Carga los dispositivos con puertos y reconstruye el grafo de conexiones.
 * SVG con pan (drag) + zoom (rueda). Los nodos son clicables (detalle).
 * Los cables se muestran con su color real y se agrupan (multi-cable).
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LocationFilter, { matchesLocation } from './LocationFilter';

// ── Device type registry ──────────────────────────────────────────────────────
// infrastructure types (we load these fully)
const INFRA_TYPES = {
  switch: {
    label: 'Switch', icon: '🔀', layer: 0,
    fill: 'var(--bg-input)', stroke: 'var(--accent)', text: 'var(--accent-dark)',
    loadDevices: cid => api.getElements(cid, { element_type: 'switch' }).then(r => r.data || []),
    loadPorts:   (id, cid) => api.getPorts(id, cid).then(r => r.data || []),
  },
  panel: {
    label: 'Patch Panel', icon: '🔗', layer: 1,
    fill: 'var(--bg-toolbar)', stroke: 'var(--accent-2)', text: 'var(--accent-2)',
    loadDevices: cid => api.getElements(cid, { element_type: 'panel' }).then(r => r.data || []),
    loadPorts:   (id, cid) => api.getPorts(id, cid).then(r => r.data || []),
  },
  server: {
    label: 'Servidor', icon: '🗄️', layer: 2,
    fill: 'var(--green-50)', stroke: 'var(--green-600)', text: 'var(--green-600)',
    loadDevices: cid => api.getServers(cid).then(r => r.data || []),
    loadPorts:   (id, cid) => api.getPorts(id, cid).then(r => (r.data || []).filter(p => p.component_type === 'nic')),
  },
};

// terminal device types (appear as small endpoint nodes from other_end data)
const TERMINAL_META = {
  workstation: { label: 'PC / Portátil', icon: '💻', fill: 'var(--amber-50)', stroke: 'var(--amber-600)', text: 'var(--amber-600)' },
  ip_phone:    { label: 'Teléfono IP',   icon: '📞', fill: 'var(--warn-bg)', stroke: 'var(--accent-2)', text: 'var(--accent-2)' },
  printer:     { label: 'Impresora',     icon: '🖨️',  fill: 'var(--warn-bg)', stroke: 'var(--warn-text)', text: 'var(--warn-text)' },
  wall_outlet: { label: 'Toma de red',   icon: '🔌', fill: 'var(--green-50)', stroke: 'var(--green-600)', text: 'var(--green-600)' },
  default:     { label: 'Dispositivo',   icon: '📟', fill: 'var(--bg-toolbar)', stroke: 'var(--border)', text: 'var(--text-secondary)' },
};

// ── Layout constants ──────────────────────────────────────────────────────────
const NODE_W   = 200;
const NODE_H   = 68;
const TERM_W   = 140;
const TERM_H   = 44;
const H_GAP    = 36;    // gap between nodes in the same layer
const LAYER_Y  = { 0: 80, 1: 270, 2: 460, 3: 640 };
const START_X  = 70;

// ── Cable colors ──────────────────────────────────────────────────────────────
const CABLE_HEX = {
  negro: 'var(--text-primary)', gris: 'var(--text-muted)', blanco: 'var(--border)', rojo: 'var(--red-600)',
  azul: 'var(--accent)', verde: 'var(--green-600)', amarillo: 'var(--amber-600)', naranja: 'var(--warn-text)',
  violeta: 'var(--accent-2)', celeste: 'var(--accent-l)',
};
const cHex = name => CABLE_HEX[name] || 'var(--border)';

// ── Graph builder ─────────────────────────────────────────────────────────────
function buildGraph(devicePortsMap) {
  // devicePortsMap: { switch: [{device, ports}], panel: [...], server: [...] }

  // ── Build infra nodes
  const nodes = new Map();
  Object.entries(INFRA_TYPES).forEach(([typeKey, def]) => {
    const arr = devicePortsMap[typeKey] || [];
    arr.forEach((item, i) => {
      const x = START_X + i * (NODE_W + H_GAP);
      const y = LAYER_Y[def.layer];
      const key = `infra:${typeKey}:${item.device.id}`;
      const total = item.ports.length;
      let h = NODE_H;
      if (total > 0) {
        const portCols = Math.min(total, Math.max(8, Math.ceil(Math.sqrt(total * 3))));
        const portRows = Math.ceil(total / portCols);
        h = 66 + portRows * 8.4 + 6; // base (util bar at 48) + port rows + padding
      }
      nodes.set(key, { key, kind: 'infra', typeKey, device: item.device, ports: item.ports, x, y, w: NODE_W, h });
    });
  });

  // ── Collect all connections → edges between infra nodes or infra→terminal
  const edgeMap = new Map();
  const terminalRaw = new Map(); // key `terminal:type:id` → info

  const addEdge = (aKey, bKey, cable) => {
    const [k1, k2] = [aKey, bKey].sort();
    const eKey = `${k1}===${k2}`;
    if (!edgeMap.has(eKey)) edgeMap.set(eKey, { fromKey: k1, toKey: k2, cables: [] });
    const e = edgeMap.get(eKey);
    if (cable && !e.cables.find(c => c.id === cable.id)) e.cables.push(cable);
  };

  Object.entries(INFRA_TYPES).forEach(([typeKey]) => {
    const arr = devicePortsMap[typeKey] || [];
    arr.forEach(({ device, ports }) => {
      const fromKey = `infra:${typeKey}:${device.id}`;
      ports.forEach(port => {
        const conn = port.connection;
        if (!conn?.other_end) return;
        const oe  = conn.other_end;
        const eid = oe.element_id;
        const et  = oe.element_type || 'default';
        if (!eid) return;

        const cable = {
          id: conn.id, cable_type: conn.cable_type, cable_color: conn.cable_color,
          cable_length_m: conn.cable_length_m,
          fromPort: port.label || `P${port.port_number}`,
          toPort:   oe.label   || `P${oe.port_number}`,
        };

        if (INFRA_TYPES[et]) {
          // infra → infra
          const toKey = `infra:${et}:${eid}`;
          if (nodes.has(toKey)) addEdge(fromKey, toKey, cable);
        } else {
          // infra → terminal endpoint
          const tKey = `terminal:${et}:${eid}`;
          if (!terminalRaw.has(tKey)) {
            terminalRaw.set(tKey, { key: tKey, kind: 'terminal', typeKey: et, id: eid, name: oe.element_name || `${et} #${eid}` });
          }
          addEdge(fromKey, tKey, cable);
        }
      });
    });
  });

  // ── Position terminal nodes (layer 3), grouped by type
  const terminalGroups = {};
  terminalRaw.forEach(t => {
    if (!terminalGroups[t.typeKey]) terminalGroups[t.typeKey] = [];
    terminalGroups[t.typeKey].push(t);
  });
  let termX = START_X;
  Object.values(terminalGroups).forEach(group => {
    group.forEach(t => {
      nodes.set(t.key, { ...t, x: termX, y: LAYER_Y[3], w: TERM_W, h: TERM_H });
      termX += TERM_W + H_GAP;
    });
  });

  return { nodes, edges: [...edgeMap.values()] };
}

// ── SVG helpers ───────────────────────────────────────────────────────────────
function nodeAnchor(node, dir) {
  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;
  if (dir === 'top')    return { x: cx, y: node.y };
  if (dir === 'bottom') return { x: cx, y: node.y + node.h };
  if (dir === 'left')   return { x: node.x, y: cy };
  if (dir === 'right')  return { x: node.x + node.w, y: cy };
  return { x: cx, y: cy };
}

function edgeAnchors(from, to) {
  if (!from || !to) return null;
  if (from.y < to.y)  return { p1: nodeAnchor(from, 'bottom'), p2: nodeAnchor(to, 'top') };
  if (from.y > to.y)  return { p1: nodeAnchor(from, 'top'),    p2: nodeAnchor(to, 'bottom') };
  // same layer: side connection
  if (from.x < to.x)  return { p1: nodeAnchor(from, 'right'),  p2: nodeAnchor(to, 'left') };
  return                      { p1: nodeAnchor(from, 'left'),   p2: nodeAnchor(to, 'right') };
}

function bezierD(p1, p2, sameLvl) {
  const dx = Math.abs(p2.x - p1.x);
  const dy = Math.abs(p2.y - p1.y);
  if (sameLvl) {
    const bend = Math.min(80, dx * 0.5);
    return `M${p1.x},${p1.y} C${p1.x + bend},${p1.y} ${p2.x - bend},${p2.y} ${p2.x},${p2.y}`;
  }
  const cy = (p1.y + p2.y) / 2;
  return `M${p1.x},${p1.y} C${p1.x},${cy} ${p2.x},${cy} ${p2.x},${p2.y}`;
}

// ── SVG: Infra Node ───────────────────────────────────────────────────────────
function InfraNode({ node, selected, onClick }) {
  const def = INFRA_TYPES[node.typeKey];
  const used  = node.ports.filter(p => p.connection).length;
  const total = node.ports.length;
  const pct   = total > 0 ? used / total : 0;
  const barClr = pct > 0.85 ? 'var(--red-600)' : pct > 0.5 ? 'var(--amber-600)' : 'var(--green-600)';

  // Port circles layout
  const portCols = Math.min(total, Math.max(8, Math.ceil(Math.sqrt(total * 3))));
  const portR = 3.2;
  const portGap = 2;
  const portStartX = node.x + 10;
  const portStartY = node.y + 62;

  return (
    <g onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      {/* Drop shadow */}
      <rect x={node.x + 3} y={node.y + 3} width={node.w} height={node.h} rx={10} fill="var(--text-primary)" opacity="0.08" />
      {/* Body */}
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={10}
        fill={def.fill}
        stroke={selected ? def.text : def.stroke}
        strokeWidth={selected ? 2.5 : 1.5}
      />
      {/* Accent bar top */}
      <rect x={node.x + 1} y={node.y + 1} width={node.w - 2} height={4} rx={4} fill={def.stroke} opacity={0.4} />
      {/* Icon */}
      <text x={node.x + 11} y={node.y + 30} fontSize={18}>{def.icon}</text>
      {/* Name */}
      <text x={node.x + 36} y={node.y + 24} fontSize={11} fontWeight={700} fill={def.text}>
        {node.device.name.length > 22 ? node.device.name.slice(0, 21) + '…' : node.device.name}
      </text>
      {/* Type */}
      <text x={node.x + 36} y={node.y + 38} fontSize={9} fill={def.text} opacity={0.65}>{def.label}</text>
      {/* Port util bar */}
      {total > 0 && <>
        <rect x={node.x + 10} y={node.y + 48} width={node.w - 20} height={5} rx={2.5} fill="var(--slate-300)" />
        <rect x={node.x + 10} y={node.y + 48} width={(node.w - 20) * pct} height={5} rx={2.5} fill={barClr} />
        <text x={node.x + node.w - 8} y={node.y + 46} fontSize={7.5} fill={def.text} opacity={0.6} textAnchor="end">
          {used}/{total}
        </text>
      </>}
      {/* Port circles */}
      {total > 0 && node.ports.map((port, idx) => {
        const col = idx % portCols;
        const row = Math.floor(idx / portCols);
        const cx = portStartX + col * (portR * 2 + portGap) + portR;
        const cy = portStartY + row * (portR * 2 + portGap) + portR;
        const conn = port.connection;
        const fill = conn ? cHex(conn.cable_color) : 'var(--slate-300)';
        const stroke = conn ? 'var(--bg-card)' : 'var(--border)';
        const label = port.label || `P${port.port_number}`;
        return (
          <circle key={port.id || idx} cx={cx} cy={cy} r={portR}
            fill={fill} stroke={stroke} strokeWidth={0.5}>
            <title>{label}{conn ? ` → ${conn.other_end?.element_name || '?'} (${conn.cable_type || ''} ${conn.cable_color || ''})` : ' (libre)'}</title>
          </circle>
        );
      })}
    </g>
  );
}

// ── SVG: Terminal Node ────────────────────────────────────────────────────────
function TerminalNode({ node, selected, onClick }) {
  const m = TERMINAL_META[node.typeKey] || TERMINAL_META.default;
  const nm = node.name.length > 16 ? node.name.slice(0, 15) + '…' : node.name;
  return (
    <g onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={8}
        fill={m.fill}
        stroke={selected ? m.text : m.stroke}
        strokeWidth={selected ? 2 : 1}
      />
      <text x={node.x + 8} y={node.y + 20} fontSize={13}>{m.icon}</text>
      <text x={node.x + 26} y={node.y + 20} fontSize={10} fontWeight={600} fill={m.text}>{nm}</text>
      <text x={node.x + 8} y={node.y + 34} fontSize={8} fill={m.text} opacity={0.65}>{m.label}</text>
    </g>
  );
}

// ── SVG: Edge ─────────────────────────────────────────────────────────────────
function NetEdge({ edge, nodeMap, selected, onEnter, onLeave, onClick }) {
  const from = nodeMap.get(edge.fromKey);
  const to   = nodeMap.get(edge.toKey);
  if (!from || !to) return null;

  const sameLvl = from.y === to.y;
  const anch = edgeAnchors(from, to);
  if (!anch) return null;
  const { p1, p2 } = anch;
  const d = bezierD(p1, p2, sameLvl);

  // Color: dominant cable color or neutral
  const colorCounts = {};
  edge.cables.forEach(c => { if (c.cable_color) colorCounts[c.cable_color] = (colorCounts[c.cable_color] || 0) + 1; });
  const dominant = Object.entries(colorCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const color = selected ? 'var(--amber-600)' : (dominant ? cHex(dominant) : 'var(--border)');
  const sw = Math.min(5, 1.5 + edge.cables.length * 0.6);

  // Badge midpoint
  const bmx = (p1.x + p2.x) / 2;
  const bmy = sameLvl ? p1.y - 18 : (p1.y + p2.y) / 2;

  const isTerminalEdge = to.kind === 'terminal' || from.kind === 'terminal';

  return (
    <g>
      {/* Visible */}
      <path d={d} fill="none"
        stroke={isTerminalEdge ? 'var(--border)' : color}
        strokeWidth={isTerminalEdge ? 1.5 : sw}
        strokeDasharray={isTerminalEdge ? '5 3' : 'none'}
        opacity={isTerminalEdge ? 0.5 : 0.75}
      />
      {/* Hit area */}
      <path d={d} fill="none" stroke="transparent" strokeWidth={16}
        style={{ cursor: 'pointer', pointerEvents: 'visibleStroke' }}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onClick={onClick}
      />
      {/* Multi-cable badge */}
      {edge.cables.length > 1 && !isTerminalEdge && (
        <g>
          <circle cx={bmx} cy={bmy} r={10} fill={selected ? 'var(--amber-600)' : color} opacity={0.9} />
          <text x={bmx} y={bmy + 4} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--text-primary)">
            {edge.cables.length}
          </text>
        </g>
      )}
    </g>
  );
}

// ── SVG: Layer band ───────────────────────────────────────────────────────────
function LayerBand({ typeKey, nodes }) {
  const def = INFRA_TYPES[typeKey];
  if (!def) return null;
  const layer = [...nodes.values()].filter(n => n.kind === 'infra' && n.typeKey === typeKey);
  if (!layer.length) return null;
  const xs = layer.flatMap(n => [n.x, n.x + n.w]);
  const minX = Math.min(...xs) - 24;
  const maxX = Math.max(...xs) + 24;
  const maxH = Math.max(...layer.map(n => n.h));
  const y    = LAYER_Y[def.layer];
  return (
    <g pointerEvents="none">
      <rect x={minX} y={y - 18} width={maxX - minX} height={maxH + 36}
        rx={14} fill={def.fill} stroke={def.stroke} strokeWidth={1}
        opacity={0.22} strokeDasharray="7 4" />
      <text x={minX + 10} y={y - 22} fontSize={9.5} fontWeight={700} fill={def.stroke} opacity={0.9}>
        {def.icon} {def.label.toUpperCase()}S
      </text>
    </g>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
function Tooltip({ data, nodeMap, pos }) {
  if (!data || !pos) return null;
  const lines = [];
  if (data.kind === 'node') {
    const n = data.node;
    if (n.kind === 'infra') {
      const def = INFRA_TYPES[n.typeKey];
      lines.push(['Dispositivo', `${def.icon} ${def.label}`]);
      lines.push(['Nombre', n.device.name]);
      const used = n.ports.filter(p => p.connection).length;
      lines.push(['Puertos', `${used}/${n.ports.length} usados`]);
    } else {
      const m = TERMINAL_META[n.typeKey] || TERMINAL_META.default;
      lines.push(['Tipo', `${m.icon} ${m.label}`]);
      lines.push(['Nombre', n.name]);
    }
  } else if (data.kind === 'edge') {
    const { edge } = data;
    const from = nodeMap.get(edge.fromKey);
    const to   = nodeMap.get(edge.toKey);
    const fn = from?.kind === 'infra' ? from.device.name : from?.name;
    const tn = to?.kind   === 'infra' ? to.device.name   : to?.name;
    lines.push(['Conexión', `${fn || '?'} ↔ ${tn || '?'}`]);
    lines.push(['Cables', edge.cables.length]);
    edge.cables.slice(0, 5).forEach((c, i) => {
      const desc = [c.cable_type, c.cable_color, c.cable_length_m ? `${c.cable_length_m}m` : null].filter(Boolean).join(' · ');
      lines.push([`#${i + 1}`, desc || '—']);
    });
    if (edge.cables.length > 5) lines.push(['', `…+${edge.cables.length - 5} más`]);
  }
  return (
    <div style={{
      position: 'fixed', left: pos.x + 14, top: Math.max(8, pos.y - 20),
        background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--slate-300)', borderRadius: 9,
      padding: '9px 13px', fontSize: 12, pointerEvents: 'none', zIndex: 9999,
      minWidth: 190, maxWidth: 280,
    }}>
      {lines.map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
          <span style={{ color: 'var(--text-muted)', minWidth: 72, flexShrink: 0 }}>{k}</span>
          <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function NetworkTopologyCanvas({ companyId }) {
  const nav = useNavigate();
  const [loading,       setLoading]       = useState(true);
  const [devicePortsMap, setDevicePortsMap] = useState({});
  const [selected,      setSelected]      = useState(null);
  const [tooltip,       setTooltip]       = useState(null);
  const [showTerminals, setShowTerminals] = useState(true);
  const [locationFilter, setLocationFilter] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cmdb.net.locations') || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch { return []; }
  });
  const [locations, setLocations] = useState([]);
  const [vp, setVp] = useState({ x: 0, y: 0, scale: 1 });
  const vpRef    = useRef(vp);
  useEffect(() => { vpRef.current = vp; }, [vp]);
  const dragging = useRef(null);
  const nodeDrag = useRef(null);
  const [nodePositions, setNodePositions] = useState({});
  const svgRef   = useRef();

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all(
      Object.entries(INFRA_TYPES).map(async ([typeKey, def]) => {
        const devices = await def.loadDevices(companyId);
        const withPorts = await Promise.all(
          devices.map(async device => {
            try { return { device, ports: await def.loadPorts(device.id, companyId) }; }
            catch { return { device, ports: [] }; }
          })
        );
        return [typeKey, withPorts];
      })
    ).then(res => setDevicePortsMap(Object.fromEntries(res)))
     .catch(() => {})
     .finally(() => setLoading(false));

    api.getLocations('', companyId)
      .then(res => setLocations(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []))
      .catch(() => setLocations([]));
  }, [companyId]);

  const { nodes: allNodes, edges: allEdges } = useMemo(
    () => buildGraph(devicePortsMap),
    [devicePortsMap]
  );

  // Filter out terminal nodes when hidden, and by location.
  const { nodes, edges } = useMemo(() => {
    let filtered = new Map(allNodes);
    if (!showTerminals) {
      filtered = new Map([...filtered].filter(([, n]) => n.kind !== 'terminal'));
    }
    if (locationFilter.length) {
      filtered = new Map(
        [...filtered].filter(([, n]) => {
          if (n.kind === 'terminal') return true; // se caen si su enlace desaparece
          return matchesLocation(locations, locationFilter, n.device?.location_id);
        }),
      );
    }
    let filteredEdges = allEdges.filter(e => filtered.has(e.fromKey) && filtered.has(e.toKey));
    if (locationFilter.length) {
      // Los equipos finales que se quedan sin enlace no se pintan.
      const linked = new Set();
      filteredEdges.forEach(e => { linked.add(e.fromKey); linked.add(e.toKey); });
      filtered = new Map([...filtered].filter(([k, n]) => n.kind !== 'terminal' || linked.has(k)));
      filteredEdges = filteredEdges.filter(e => filtered.has(e.fromKey) && filtered.has(e.toKey));
    }
    return { nodes: filtered, edges: filteredEdges };
  }, [allNodes, allEdges, showTerminals, locationFilter, locations]);

  // Apply node position overrides (from dragging)
  const posNodes = useMemo(() => {
    const m = new Map();
    nodes.forEach((n, k) => {
      const ov = nodePositions[k];
      m.set(k, ov ? { ...n, x: ov.x, y: ov.y } : n);
    });
    return m;
  }, [nodes, nodePositions]);

  const infraNodes    = useMemo(() => [...posNodes.values()].filter(n => n.kind === 'infra'),    [posNodes]);
  const terminalNodes = useMemo(() => [...posNodes.values()].filter(n => n.kind === 'terminal'), [posNodes]);

  // Stats
  const totalCables      = allEdges.reduce((s, e) => s + e.cables.length, 0);
  const infraEdgeCount   = allEdges.filter(e => {
    const f = allNodes.get(e.fromKey); const t = allNodes.get(e.toKey);
    return f?.kind === 'infra' && t?.kind === 'infra';
  }).length;

  const bbox = useMemo(() => {
    const all = [...nodes.values()];
    if (!all.length) return { minX: 0, minY: 0, maxX: 1000, maxY: 700 };
    const xs = all.flatMap(n => [n.x, n.x + n.w]);
    const ys = all.flatMap(n => [n.y, n.y + n.h]);
    return { minX: Math.min(...xs) - 60, minY: Math.min(...ys) - 60, maxX: Math.max(...xs) + 80, maxY: Math.max(...ys) + 80 };
  }, [nodes]);

  const handleFit = useCallback(() => {
    if (!svgRef.current) return;
    const { width: W, height: H } = svgRef.current.getBoundingClientRect();
    const sw = bbox.maxX - bbox.minX, sh = bbox.maxY - bbox.minY;
    const scale = Math.min(1.6, Math.min(W / sw, H / sh) * 0.92);
    setVp({ x: W / 2 - (bbox.minX + sw / 2) * scale, y: H / 2 - (bbox.minY + sh / 2) * scale, scale });
  }, [bbox]);

  useEffect(() => { if (!loading && nodes.size) setTimeout(handleFit, 60); }, [loading, nodes.size, handleFit]);

  // Wheel zoom – native listener with { passive: false } to allow preventDefault
  const onWheel = useCallback(e => {
    e.preventDefault();
    const f = e.deltaY < 0 ? 1.12 : 0.9;
    setVp(v => {
      const ns = Math.max(0.12, Math.min(5, v.scale * f));
      const r  = svgRef.current?.getBoundingClientRect();
      if (!r) return v;
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      return { scale: ns, x: mx - (mx - v.x) * (ns / v.scale), y: my - (my - v.y) * (ns / v.scale) };
    });
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const onMouseDown = useCallback(e => {
    const nodeEl = e.target.closest('g[data-node]');
    if (nodeEl) {
      // Start node drag
      const key = nodeEl.getAttribute('data-node');
      const node = posNodes.get(key);
      if (!node) return;
      e.preventDefault();
      const cur = vpRef.current;
      nodeDrag.current = {
        key,
        startX: e.clientX,
        startY: e.clientY,
        origX: node.x,
        origY: node.y,
        scale: cur.scale,
      };
      return;
    }
    // Canvas pan
    e.preventDefault();
    const cur = vpRef.current;
    dragging.current = { sx: e.clientX - cur.x, sy: e.clientY - cur.y };
  }, [posNodes]);

  const onMouseMove = useCallback(e => {
    if (nodeDrag.current) {
      const nd = nodeDrag.current;
      const dx = (e.clientX - nd.startX) / nd.scale;
      const dy = (e.clientY - nd.startY) / nd.scale;
      setNodePositions(prev => ({ ...prev, [nd.key]: { x: nd.origX + dx, y: nd.origY + dy } }));
      return;
    }
    const d = dragging.current;
    if (!d) return;
    setVp(v => ({ ...v, x: e.clientX - d.sx, y: e.clientY - d.sy }));
  }, []);

  const stopDrag = useCallback(() => { dragging.current = null; nodeDrag.current = null; }, []);

  const handleNodeClick = useCallback(node => {
    setSelected(s => s?.type === 'node' && s.key === node.key ? null : { type: 'node', key: node.key });
    setTooltip(null);
  }, []);

  const handleEdgeClick = useCallback(edge => {
    const eKey = `${edge.fromKey}===${edge.toKey}`;
    setSelected(s => s?.type === 'edge' && s.key === eKey ? null : { type: 'edge', key: eKey });
    setTooltip(null);
  }, []);

  const legend = [
    ...Object.entries(INFRA_TYPES).map(([k, d]) => ({ clr: d.stroke, label: d.label, count: (devicePortsMap[k] || []).length })),
    { clr: 'var(--text-muted)', label: 'Terminales', count: [...allNodes.values()].filter(n => n.kind === 'terminal').length },
  ];

  if (!companyId) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Selecciona una empresa.</div>;

  return (
    <div className="ntop-page" style={{
      height: 'calc(100vh - 60px)', margin: '-2rem',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', overflow: 'hidden',
    }}>
      {/* ── Toolbar ── */}
      <div style={{
        padding: '8px 16px', background: 'var(--bg-card)', borderBottom: '1px solid var(--slate-300)',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap',
      }}>
        <span style={{ fontWeight: 700, fontSize: '0.97rem', color: 'var(--text-primary)' }}>🌐 Topología de Red</span>
        <div style={{ flexGrow: 1 }} />
        {/* Legend */}
        <div className="ntop-legend" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 }}>
          {legend.map(l => (
            <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: l.clr, display: 'inline-block' }} />
              {l.label} ({l.count})
            </span>
          ))}
        </div>
        {/* Terminals toggle */}
        <LocationFilter
          companyId={companyId}
          value={locationFilter}
          onChange={(ids) => {
            setLocationFilter(ids);
            try { localStorage.setItem('cmdb.net.locations', JSON.stringify(ids)); } catch { /* noop */ }
          }}
        />
        <button
          onClick={() => setShowTerminals(t => !t)}
          style={{
            padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: 'pointer',
            background: showTerminals ? 'var(--amber-50)' : 'var(--bg-input)',
            color: showTerminals ? 'var(--amber-600)' : 'var(--text-muted)',
            border: `1px solid ${showTerminals ? 'var(--amber-600)' : 'var(--slate-300)'}`,
          }}
        >
          💻 Terminales {showTerminals ? 'ON' : 'OFF'}
        </button>
        <button onClick={handleFit}                                                        style={btnS('var(--accent)')}>⤢ Ajustar</button>
        <button onClick={() => setVp(v => ({ ...v, scale: Math.min(5,    v.scale * 1.2) }))} style={btnS('var(--text-secondary)')}>+</button>
        <button onClick={() => setVp(v => ({ ...v, scale: Math.max(0.12, v.scale / 1.2) }))} style={btnS('var(--text-secondary)')}>−</button>
        <button onClick={() => nav('/infrastructure/network-canvas')}  style={btnS('var(--text-secondary)', true)}>🔀 Canvas cableado</button>
        <button onClick={() => nav('/infrastructure/switches')}         style={btnS('var(--text-secondary)', true)}>🔀 Switches</button>
      </div>

      {/* ── Stats bar ── */}
      {!loading && (
        <div style={{
          padding: '5px 16px', background: 'var(--bg-toolbar)', borderBottom: '1px solid var(--slate-300)',
          display: 'flex', gap: 22, fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap',
        }}>
          <span>🔀 <strong>{(devicePortsMap.switch || []).length}</strong> switches</span>
          <span>🔗 <strong>{(devicePortsMap.panel || []).length}</strong> patch panels</span>
          <span>🗄️ <strong>{(devicePortsMap.server || []).length}</strong> servidores</span>
          <span>🌐 <strong>{infraEdgeCount}</strong> enlaces infra</span>
          <span>🪢 <strong>{totalCables}</strong> cables</span>
          {terminalNodes.length > 0 && <span>💻 <strong>{terminalNodes.length}</strong> terminales</span>}
          {selected && (
            <span style={{ color: 'var(--accent-2)', cursor: 'pointer', marginLeft: 'auto', fontWeight: 600 }}
              onClick={() => setSelected(null)}>✕ Limpiar selección</span>
          )}
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
          <span className="spinner-border spinner-border-sm me-2" /> Cargando topología…
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && nodes.size === 0 && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 52, opacity: 0.25 }}>🌐</div>
          <div style={{ fontWeight: 600, fontSize: 16 }}>Sin infraestructura de red registrada</div>
          <div style={{ fontSize: 13 }}>Añade switches, patch panels o servidores con sus cables</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={() => nav('/infrastructure/switches')}     style={btnS('var(--accent)')}>→ Switches</button>
            <button onClick={() => nav('/infrastructure/patch-panels')} style={btnS('var(--accent-2)')}>→ Patch Panels</button>
            <button onClick={() => nav('/infrastructure/servers')}      style={btnS('var(--green-600)')}>→ Servidores</button>
          </div>
        </div>
      )}

      {/* ── Canvas ── */}
      {!loading && nodes.size > 0 && (
        <svg
          ref={svgRef}
          className="ntop-svg"
          style={{ flex: 1, cursor: 'grab', userSelect: 'none', width: '100%', maxWidth: '100%' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          <defs>
            <pattern id="ntopgrid" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="var(--border)" opacity="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ntopgrid)" />

          <g transform={`translate(${vp.x},${vp.y}) scale(${vp.scale})`}>

            {/* Layer bands */}
            {Object.keys(INFRA_TYPES).map(tk => (
              <LayerBand key={tk} typeKey={tk} nodes={nodes} />
            ))}

            {/* Terminal zone band */}
            {showTerminals && terminalNodes.length > 0 && (
              <g pointerEvents="none">
                <rect
                  x={START_X - 20}
                  y={LAYER_Y[3] - 18}
                  width={terminalNodes.reduce((mx, n) => Math.max(mx, n.x + n.w), 0) - START_X + 60}
                  height={TERM_H + 36}
                  rx={12} fill="var(--bg-toolbar)" stroke="var(--border)" strokeWidth={1}
                  opacity={0.4} strokeDasharray="5 4"
                />
                <text x={START_X - 10} y={LAYER_Y[3] - 22} fontSize={9.5} fontWeight={700} fill="var(--text-muted)">
                  💻 DISPOSITIVOS TERMINALES
                </text>
              </g>
            )}

            {/* Edges (drawn behind nodes) */}
            {edges.map(edge => {
              const eKey = `${edge.fromKey}===${edge.toKey}`;
              return (
                <NetEdge
                  key={eKey}
                  edge={edge}
                  nodeMap={posNodes}
                  selected={selected?.type === 'edge' && selected.key === eKey}
                  onEnter={e => setTooltip({ kind: 'edge', edge, pos: { x: e.clientX, y: e.clientY } })}
                  onLeave={() => setTooltip(null)}
                  onClick={() => handleEdgeClick(edge)}
                />
              );
            })}

            {/* Infra nodes */}
            {infraNodes.map(node => (
              <g key={node.key} data-node={node.key} style={{ cursor: 'grab' }}>
                <InfraNode
                  node={node}
                  selected={selected?.type === 'node' && selected.key === node.key}
                  onClick={handleNodeClick}
                />
              </g>
            ))}

            {/* Terminal nodes */}
            {showTerminals && terminalNodes.map(node => (
              <g key={node.key} data-node={node.key} style={{ cursor: 'grab' }}>
                <TerminalNode
                  node={node}
                  selected={selected?.type === 'node' && selected.key === node.key}
                  onClick={handleNodeClick}
                />
              </g>
            ))}
          </g>
        </svg>
      )}

      {/* Tooltip */}
      {tooltip && (
        <Tooltip
          data={{ kind: tooltip.kind, node: tooltip.node, edge: tooltip.edge }}
          nodeMap={posNodes}
          pos={tooltip.pos}
        />
      )}
    </div>
  );
}

function btnS(color, outline = false) {
  return {
    padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    background: outline ? 'var(--bg-input)' : color,
    color: outline ? color : 'var(--text-primary)',
    border: `1px solid ${outline ? 'var(--slate-300)' : color}`,
  };
}
